import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';

import styles from './MobileMenu.scss';
import { SubMenu } from '../SubMenu/SubMenu';
import { filterMenuData } from '../../../../utils/menu/filterMenuData';
import { helpData } from '../tabbedMenuData';
import { Category, SHOP_DEPARTMENT, EXTRAS_DEPARTMENT } from '../../constants';
import { MobileMenuFooter } from '../MobileMenuFooter/MobileMenuFooter';

type MobileMenuProps = {
  data: MenuItem[];
  fetchChildCategories: (params: { id: string | undefined }) => Promise<{
    child?: MenuItem[];
    error?: unknown;
  }>;
  typeaheadIsOpen?: boolean;
  isVisible?: boolean;
  onAnimationComplete?: () => void;
};

export const MobileMenu = ({
  data,
  fetchChildCategories,
  typeaheadIsOpen = false,
  isVisible = true,
  onAnimationComplete = () => {},
}: MobileMenuProps) => {
  const mobileMenuItems = filterMenuData(data, ['MOBILE_WEB']) as MenuItem[];

  const [rootMenuItems, setRootMenuItems] =
    useState<MenuItem[]>(mobileMenuItems);

  const top = useRef<HTMLElement | null>(null);
  const [activeLevel, setActiveLevel] = useState(0);

  const scrollToTop = () => {
    if (top.current && top.current.scrollBy) {
      top.current.scrollBy(0, -2000);
    }
  };

  const [activeTab, setActiveTab] = useState<Category>(Category.SHOP);

  const [animationKey, setAnimationKey] = useState(0);
  const prevIsVisible = useRef(isVisible);
  const closeAnimationDurationMs = 500;

  useEffect(function remountNavAndForceOpenAnimation() {
    setAnimationKey(prev => prev + 1);
  }, []);

  // Notify parent once close animation has completed so it can unmount the menu.
  useEffect(
    function handleCloseAnimation() {
      const shouldBeClosing = !isVisible && prevIsVisible.current === true;

      if (shouldBeClosing) {
        const timer = setTimeout(() => {
          prevIsVisible.current = false;
          onAnimationComplete();
        }, closeAnimationDurationMs); // 300ms for animation + buffer

        return function clearCloseTimer() {
          clearTimeout(timer);
        };
      }

      if (isVisible) {
        prevIsVisible.current = true;
      }
    },
    [isVisible, onAnimationComplete]
  );

  useEffect(
    function syncRootMenuWithActiveTab() {
      setRootMenuItems(data.find(item => item.name === activeTab)?.child || []);
    },
    [activeTab]
  );

  const mounted = useRef(false);

  useEffect(function trackMountedState() {
    mounted.current = true;
    return function markAsUnmounted() {
      mounted.current = false;
    };
  }, []);

  const fetchSubMenuData = async (
    menuItemId: string | undefined,
    menuIndex: number
  ) => {
    if (rootMenuItems[menuIndex]?.isCached) {
      return;
    }

    try {
      const fetchedData = await fetchChildCategories({
        id: menuItemId,
      });

      if (mounted.current === false) {
        return;
      }

      if (!fetchedData || fetchedData.error) {
        throw new Error('Fetch failed');
      }

      setRootMenuItems(existingRootMenuItems =>
        existingRootMenuItems.map((existing, i) =>
          i === menuIndex
            ? {
                ...existing,
                child: (fetchedData.child || []) as MenuItem[],
                isCached: true,
              }
            : existing
        )
      );
    } catch {
      console.error(`fetch failed for category id: ${menuItemId}`);
    }
  };

  const activeMenuStyles = [
    styles.activeLevelRoot,
    styles.activeLevelFirst,
    styles.activeLevelSecond,
    styles.activeLevelThird,
  ];
  const subMenuClassName = classNames([
    styles.subMenu,
    activeMenuStyles[activeLevel],
  ]);

  const shouldBeClosing = !isVisible && prevIsVisible.current === true;

  const servicesMenuItem = data.find(item => item.name === Category.SERVICES);

  const footerMenuItems: MenuItem[] = [
    ...(servicesMenuItem ? [servicesMenuItem] : []),
    {
      ...helpData,
      isCached: true,
      includedInMenus: ['MOBILE_WEB'],
      type: 'HEADER',
    },
  ];

  const combinedShopMenuItems: MenuItem[] = [
    {
      id: 'shop-department',
      name: SHOP_DEPARTMENT,
      child: rootMenuItems,
      includedInMenus: ['MOBILE_WEB'],
      longColumnOnFlyout: false,
      experimentNames: [],
      type: 'HEADER',
    },
    {
      id: 'extras-department',
      name: EXTRAS_DEPARTMENT,
      child: footerMenuItems,
      includedInMenus: ['MOBILE_WEB'],
      longColumnOnFlyout: false,
      experimentNames: [],
      type: 'HEADER',
    },
  ];

  return (
    <>
      <nav
        className={classNames(styles.mobileMenu, {
          [styles.mobileMenuTypeaheadOpen]: typeaheadIsOpen,
          [styles.mobileMenuOpenAnimated]: isVisible,
          [styles.mobileMenuCloseAnimated]: shouldBeClosing,
        })}
        key={animationKey}
        ref={top}
        data-testid="mobile-navigation-menu"
      >
        <div
          className={subMenuClassName}
          data-testid="mobile-navigation-container"
        >
          <SubMenu
            level={0}
            currentActiveLevel={activeLevel}
            setActiveLevel={setActiveLevel}
            setActiveTab={setActiveTab}
            activeTab={activeTab}
            hierarchy={[activeTab]}
            subMenuData={{
              name: activeTab,
              child:
                activeTab === Category.SHOP
                  ? combinedShopMenuItems
                  : rootMenuItems,
            }}
            onItemSelected={(menuItemId, menuIndex) => {
              if (!menuItemId) {
                return;
              }
              fetchSubMenuData(menuItemId, menuIndex);
            }}
            scrollToTop={scrollToTop}
          >
            <MobileMenuFooter />
          </SubMenu>
        </div>
      </nav>
    </>
  );
};
